import { Show } from 'solid-js'
import { A, useNavigate } from '@solidjs/router'
import { userStore } from '/web/store'
import { getAssetUrl } from '/web/shared/util'
import { useBreakpoint } from '/web/shared/aifans/useBreakpoint'

const featured = {
  name: 'Jenny Cool',
  character: 'Raiden Shogun',
  link: '/model/raiden-shogun/jenny-cool/',
  img: getAssetUrl('/assets/home/home-3/bg-raiden-shogun.webp', true),
}

export default function HomeSpotlight() {
  const state = userStore()
  const loggedIn = () => state.loggedIn
  const claimed = () => !!state.profile?.hasClaimedInitialTokens
  const navigate = useNavigate()
  const breakpoint = useBreakpoint()

  return (
    <section class="relative overflow-hidden">
      {/* Background grid + glows */}
      <div
        class="pointer-events-none absolute inset-0"
        style={{
          'background-image':
            'linear-gradient(rgba(138,43,226,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(138,43,226,0.04) 1px, transparent 1px)',
          'background-size': '60px 60px',
        }}
      />
      <div
        class="pointer-events-none absolute left-[-10%] top-[-20%] h-[600px] w-[600px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(138,43,226,0.12) 0%, transparent 60%)' }}
      />
      <div
        class="pointer-events-none absolute bottom-[-10%] right-[-5%] h-[500px] w-[500px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(0,229,255,0.06) 0%, transparent 60%)' }}
      />

      <div
        class="cf-home-hero-pad cf-section relative z-10"
        style={{ 'padding-top': '48px', 'padding-bottom': '48px' }}
      >
        <div class="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
          {/* Left — Text content */}
          <div class="order-2 lg:order-1">
            {/* Featured badge */}
            <div
              class="mb-5 inline-flex items-center gap-2 rounded-full px-4 py-2"
              style={{
                background: 'rgba(255,60,172,0.15)',
                border: '1px solid rgba(255,60,172,0.3)',
              }}
            >
              <span
                class="h-[6px] w-[6px] rounded-full"
                style={{
                  background: '#FF3CAC',
                  'box-shadow': '0 0 8px #FF3CAC',
                  animation: 'cfBlink 2s ease-in-out infinite',
                }}
              />
              <span class="font-rajdhani text-[11px] font-extrabold uppercase tracking-[2px] text-[#FF3CAC]">
                Featured Creator
              </span>
            </div>

            {/* Headline — large with animated gradient on name */}
            <h1
              class="mb-4 font-bebas leading-[0.95] tracking-[1px]"
              style={{ 'font-size': 'clamp(40px, 7vw, 88px)' }}
            >
              Now Showcasing
              <br />
              <span class="hero-grad-anim">{featured.name}</span>
              <br />
              as {featured.character}
            </h1>

            {/* Profile link */}
            <A
              href={featured.link}
              class="mb-5 inline-block font-rajdhani text-[13px] font-extrabold uppercase tracking-[1.5px] text-[var(--cf-cyan)] hover:text-white"
            >
              View {featured.name}'s Profile →
            </A>

            {/* Subtitle */}
            <p class="mb-5 max-w-[480px] text-[14px] leading-relaxed text-[rgba(255,255,255,0.5)]">
              {claimed()
                ? 'Your free tokens are ready to spend. Jump into live 24/7 chats, generate stunning AI images, and unlock exclusive content from top creators.'
                : 'Create your account and instantly unlock tokens to access live chats with AI characters, exclusive content, and stunning AI image generation — completely free to start.'}
            </p>

            {/* Benefit chips */}
            <div class="mb-6 flex flex-wrap gap-2">
              {['Live 24/7 Chat', 'AI Image Gen', '1,000+ Characters'].map((t) => (
                <span class="cf-chip">
                  <span class="cf-chip-dot" />
                  {t}
                </span>
              ))}
              <Show when={breakpoint() !== 'mobile'}>
                <span class="cf-chip">
                  <span class="cf-chip-dot" />
                  VIP Content
                </span>
              </Show>
            </div>

            {/* CTA row */}
            <div class="mb-4 flex flex-wrap gap-3">
              <button
                class="cf-btn primary"
                onClick={() => {
                  if (claimed()) navigate('/generate-image/')
                  else if (loggedIn()) userStore.buyTokensModal(true)
                  else userStore.loginModal(true)
                }}
              >
                {claimed() ? 'Start Generating →' : 'Claim My Free Tokens →'}
              </button>
              <A href="/characters" class="cf-btn ghost">
                Browse Characters
              </A>
            </div>

            {/* Trust signals */}
            <div class="flex flex-wrap gap-4 font-rajdhani text-[11px] font-semibold text-[rgba(255,255,255,0.35)]">
              <span>🔒 Secure & Private</span>
              <span>{claimed() ? '✦ Tokens Ready' : '✦ Free to Join'}</span>
              <span>{claimed() ? '✦ New Drops Weekly' : '✦ No Commitment'}</span>
            </div>
          </div>

          {/* Right — Image */}
          <div class="cf-home-hero-col relative order-1 flex items-center justify-center px-2 lg:order-2">
            <div
              class="cf-hero-media relative w-full max-w-[520px] overflow-hidden rounded-3xl border border-[rgba(138,43,226,0.2)]"
              style={{
                'aspect-ratio': '3/4',
                'box-shadow': '0 20px 80px rgba(138,43,226,0.18), 0 0 120px rgba(0,229,255,0.05)',
              }}
            >
              <video
                autoplay
                loop
                muted
                playsinline
                class="h-full w-full object-cover object-top"
                poster={featured.img}
              >
                <source src="/images/raiden-hero.mp4" type="video/mp4" />
                <img
                  src={featured.img}
                  alt={`${featured.name} as ${featured.character}`}
                  class="h-full w-full object-cover object-top"
                />
              </video>

              {/* Gradient overlays */}
              <div
                class="pointer-events-none absolute inset-0"
                style={{
                  background:
                    'radial-gradient(ellipse at 20% 20%, rgba(138,43,226,0.25) 0%, transparent 50%), radial-gradient(ellipse at 90% 10%, rgba(0,229,255,0.12) 0%, transparent 50%)',
                }}
              />

              {/* Mobile badge — ref .hero-mobile-badge (phone): Rajdhani 800, 9px,
                  letter-spacing 1.8px, padding 6px 11px, gap 6px, pink glow shadow */}
              <div
                class="absolute left-3 top-3 flex items-center gap-1.5 rounded-full border border-[rgba(255,60,172,0.45)] bg-[rgba(11,11,15,0.65)] backdrop-blur-[14px] lg:hidden"
                style={{ padding: '6px 11px', 'box-shadow': '0 6px 22px rgba(255,60,172,0.18)' }}
              >
                <span
                  class="h-[5px] w-[5px] rounded-full"
                  style={{ background: '#FF3CAC', 'box-shadow': '0 0 8px #FF3CAC' }}
                />
                <span
                  class="font-rajdhani text-[9px] font-extrabold uppercase text-[#FF3CAC]"
                  style={{ 'letter-spacing': '1.8px' }}
                >
                  Featured Creator
                </span>
              </div>

              {/* NOTE: the mobile free-tokens bar was moved OUT of this overflow:hidden
                  video box (#192) and is now the global <MobileFreeTokensBar /> mounted
                  in AppShell (#216) — a position:fixed bar pinned above the bottom nav on
                  every page, so it stays in view while the page scrolls. */}
            </div>

            {/* Mobile stats card — sibling of the video box (NOT inside its
                overflow:hidden) so it can overhang the top-right corner, up & to
                the right, matching the desktop card's overhang. */}
            <div
              class="absolute -right-1 -top-1 z-10 rounded-[10px] border lg:hidden"
              style={{
                // ref .floating-stats: cyan hairline (not purple), dark bg, 10px radius
                background: 'rgba(8,6,16,0.7)',
                'border-color': 'rgba(0,229,255,0.22)',
                padding: '8px 11px',
                'backdrop-filter': 'blur(10px)',
                '-webkit-backdrop-filter': 'blur(10px)',
              }}
            >
              {[
                { num: '1,200+', label: 'Creators', color: '#8A2BE2' },
                { num: '50K+', label: 'Members', color: '#00E5FF' },
                { num: '100K+', label: 'Images', color: '#FF3CAC' },
              ].map((s) => (
                <div class="flex items-center gap-[7px] py-0.5">
                  <span
                    class="h-[6px] w-[6px] flex-shrink-0 rounded-full"
                    style={{ background: s.color, 'box-shadow': `0 0 6px ${s.color}` }}
                  />
                  <span class="font-bebas text-[12px] tracking-[0.5px] text-white">{s.num}</span>
                  {/* ref .fs-label: Rajdhani, 8px, letter-spacing 0.6px, muted #B0B0B0, weight 400 */}
                  <span class="font-rajdhani text-[8px] tracking-[0.6px] text-[#B0B0B0]">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Floating Free Tokens card — desktop only, overlapping bottom-left of image */}
            <div
              class="cf-ft-sheen-card absolute -bottom-6 -left-8 hidden cursor-pointer rounded-[14px] border border-[rgba(138,43,226,0.55)] backdrop-blur-xl lg:block"
              style={{
                animation:
                  'cfFloatY 4s ease-in-out infinite, cfFtCardGlow 3.4s ease-in-out infinite',
                padding: '22px 26px',
                width: '280px',
                'text-align': 'center',
              }}
              onClick={() => {
                if (claimed()) navigate('/generate-image/')
                else if (loggedIn()) userStore.buyTokensModal(true)
                else userStore.loginModal(true)
              }}
            >
              {/* Eyebrow */}
              <div
                class="mb-1.5 text-center font-rajdhani font-extrabold uppercase text-[#FF3CAC]"
                style={{ 'font-size': '13px', 'letter-spacing': '2.2px' }}
              >
                {claimed() ? "YOU'RE IN" : 'FREE BONUS'}
              </div>
              {/* Headline with shimmer */}
              <div
                class="mb-1.5 text-center font-bebas leading-none"
                style={{ 'font-size': '38px', 'letter-spacing': '2px' }}
              >
                <span class="ftc-headline">{claimed() ? 'TOKENS READY' : 'FREE TOKENS'}</span>
              </div>
              {/* Sub */}
              <div
                class="mb-4 text-center font-nunito"
                style={{ 'font-size': '16px', color: '#B0B0B0' }}
              >
                {claimed() ? 'Tap to start creating' : 'Unlock instantly on signup'}
              </div>
              {/* Value badge — centered below sub */}
              <div
                class="cf-ft-badge mx-auto inline-block text-center font-rajdhani font-extrabold uppercase text-white"
                style={{
                  background: '#FF3CAC',
                  'border-radius': '7px',
                  padding: '7px 14px',
                  'font-size': '14px',
                  'letter-spacing': '1px',
                  'box-shadow': '0 4px 16px rgba(255,60,172,0.5)',
                  animation: 'cfFtBadgePulse 2s ease-in-out infinite',
                }}
              >
                {claimed() ? 'CLAIMED ✓' : '$19.99 VALUE'}
              </div>
            </div>

            {/* Floating Stats card — desktop only (hangs off the upper-right corner) */}
            <div
              class="absolute -right-8 top-6 hidden rounded-xl border border-[rgba(138,43,226,0.25)] p-5 backdrop-blur-xl lg:block"
              style={{
                background: 'rgba(11,11,15,0.88)',
                'box-shadow': '0 12px 40px rgba(0,0,0,0.5)',
                animation: 'cfFloatY 4s ease-in-out infinite 1s',
              }}
            >
              {[
                { num: '1,200+', label: 'Creators', color: '#8A2BE2' },
                { num: '50K+', label: 'Members', color: '#00E5FF' },
                { num: '100K+', label: 'Images', color: '#FF3CAC' },
              ].map((s) => (
                <div class="flex items-center gap-3 py-2">
                  <span
                    class="h-[8px] w-[8px] rounded-full"
                    style={{ background: s.color, 'box-shadow': `0 0 8px ${s.color}` }}
                  />
                  <span class="font-bebas text-[20px] tracking-[0.5px] text-white">{s.num}</span>
                  <span class="font-rajdhani text-[13px] font-semibold text-[rgba(255,255,255,0.45)]">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* #216: the mobile free-tokens / tokens-ready bar moved OUT of here into the
          global <MobileFreeTokensBar /> (mounted in AppShell) so it now floats above the
          bottom nav on EVERY page, not just home. The desktop in-hero sheen card above
          (lg:block) stays here. */}

      {/* Animations */}
      <style>{`
        /* #192 mobile hero tune */
        @keyframes cfBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
        @keyframes cfFloatY { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes cfGradShift {
          0%, 100% { background-position: 0% center; }
          50% { background-position: 200% center; }
        }
        @keyframes cfFtCardGlow {
          0%, 100% {
            box-shadow: 0 14px 38px rgba(0,0,0,0.5), 0 0 30px rgba(255,60,172,0.35), 0 0 60px rgba(138,43,226,0.2);
            border-color: rgba(138,43,226,0.55);
          }
          50% {
            box-shadow: 0 14px 38px rgba(0,0,0,0.5), 0 0 40px rgba(255,60,172,0.52), 0 0 70px rgba(138,43,226,0.3);
            border-color: rgba(255,60,172,0.8);
          }
        }
        /* ── #197 shine: three layered effects, verbatim from Andy's spec ── */
        /* 1. Wordmark shimmer (.ftc-headline ≙ spec .ft-amount) */
        @keyframes ftShimmer { to { background-position: -200% center; } }
        /* 2. Card sheen — skewed translucent-white band sweeping across the card */
        @keyframes ftSheen { 0% { left: -65%; } 45%, 100% { left: 135%; } }
        /* 3. Badge pulse */
        @keyframes cfFtBadgePulse {
          0%, 100% { transform: scale(1);    box-shadow: 0 4px 16px rgba(255,60,172,0.45); }
          50%      { transform: scale(1.05); box-shadow: 0 6px 22px rgba(255,60,172,0.72); }
        }
        .hero-grad-anim {
          background: linear-gradient(135deg, #8A2BE2 0%, #00E5FF 60%, #FF3CAC 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
          animation: cfGradShift 9s ease-in-out infinite;
        }
        /* NOTE: do NOT set position here — it overrides the Tailwind 'absolute'
           overlay utility on both cards and drops them into flow (clipped by the
           hero box's overflow:hidden). The '::after' sheen anchors to the card's
           own 'absolute' positioning context. */
        .cf-ft-sheen-card {
          overflow: hidden;
          background:
            radial-gradient(150% 130% at 0% 0%, rgba(138,43,226,0.34), transparent 55%),
            radial-gradient(150% 130% at 100% 100%, rgba(0,229,255,0.22), transparent 55%),
            rgba(11,11,15,0.92) !important;
          box-shadow: 0 14px 38px rgba(0,0,0,0.5), 0 0 30px rgba(255,60,172,0.35), 0 0 60px rgba(138,43,226,0.2);
        }
        .cf-ft-sheen-card::after {
          content: "";
          position: absolute; top: 0; left: -65%;
          width: 55%; height: 100%;
          background: linear-gradient(100deg, transparent, rgba(255,255,255,0.16), transparent);
          transform: skewX(-18deg);
          animation: ftSheen 6s ease-in-out infinite;
          pointer-events: none;
        }
        /* Mobile hero-fit (#197 v2): fill the column between the top bar and the
           bottom tab bar so the video's bottom — and the free-token banner overlaid
           on it — sit just above the floating nav. Overrides the inline aspect box. */
        @media (max-width: 639.98px) {
          /* #192: match the CosplayFans export precisely. The ONLY hero spacing is
             the media container's 16px padding (ref .hero-right { padding:16px }),
             so kill the .cf-section gutter + vertical padding and give the column
             16px all around → video gets exactly 16px side + top margins. */
          /* Keep the section's horizontal gutter (shared by the video AND the
             text content below it) — only trim the vertical padding to 16px top.
             The video column drops its own px-2 so the video's side margin equals
             the 16px gutter (not gutter + 8px). */
          .cf-home-hero-pad {
            padding: 16px var(--cf-gutter) 32px !important;
          }
          .cf-home-hero-col {
            padding: 0 !important;
          }
          .cf-hero-media {
            max-width: none !important;
            aspect-ratio: auto !important;
            /* ref: 100svh - topbar - tabbar - safe-bot - 104px, cap 570 / floor 374 */
            height: calc(100svh - var(--cf-topbar-h) - var(--cf-tabbar-h) - var(--cf-safe-bot) - 104px) !important;
            max-height: 570px;
            min-height: 374px;
            border-radius: 22px !important;
          }
        }
        /* Wordmark shimmer — 4-stop gradient clipped to the glyphs, continuous slide */
        .ftc-headline {
          font-family: 'Bebas Neue', sans-serif;
          background: linear-gradient(90deg, #8A2BE2, #FF3CAC, #00E5FF, #8A2BE2);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
          animation: ftShimmer 3s linear infinite;
          display: inline-block;
        }
        /* Reduced motion — freeze all animations, static gradient fill */
        @media (prefers-reduced-motion: reduce) {
          .cf-ft-sheen-card { animation: none !important; }
          .cf-ft-sheen-card::after { animation: none !important; }
          .ftc-headline { animation: none !important; background-position: 0 center; }
          /* stylesheet !important overrides the badge's inline cfFtBadgePulse animation */
          .cf-ft-badge { animation: none !important; transform: none !important; }
        }
      `}</style>
    </section>
  )
}
